import React, { useCallback, useEffect, useRef, useState } from 'react';
import ConfirmationModal from './ConfirmationModal';

const RULES = [
  {
    test: /^release(?: payout)?$/i,
    title: 'Release payout?',
    message: 'This sends the provider payout and cannot be undone from this screen.',
    confirmText: 'Release payout',
    variant: 'success',
  },
  {
    test: /^refund\s+\$/i,
    title: 'Process refund?',
    message: 'This will return funds to the client. Verify the amount before continuing.',
    confirmText: 'Process refund',
    variant: 'danger',
  },
  {
    test: /^pay\s+[A-Z]{3}\s+\$/i,
    title: 'Confirm payment?',
    message: 'This will submit the payment using the payment method entered above.',
    confirmText: 'Confirm payment',
    variant: 'success',
  },
  {
    test: /^(?:confirm cancel|confirm cancellation|cancel booking)$/i,
    title: 'Cancel booking?',
    message: 'Cancelling changes the booking permanently and may affect payments or account standing.',
    confirmText: 'Yes, cancel',
    variant: 'danger',
  },
  {
    test: /^apply strike$/i,
    title: 'Apply account strike?',
    message: 'This adds a strike to the provider account and may affect their standing on Giggs.',
    confirmText: 'Apply strike',
    variant: 'danger',
  },
  {
    test: /^(?:force-end this job|end job now|end job)$/i,
    title: 'End this job?',
    message: 'Ending the job moves it to completion and may trigger final payment and payout steps.',
    confirmText: 'End job',
    variant: 'warning',
  },
  {
    test: /^start job$/i,
    title: 'Start this job?',
    message: 'Starting the job changes its status to in progress and records the start time.',
    confirmText: 'Start job',
    variant: 'success',
  },
  {
    test: /^(?:reject document|reject|decline)$/i,
    title: 'Confirm rejection?',
    message: 'This decision changes the current status and may not be reversible by the user.',
    confirmText: 'Confirm rejection',
    variant: 'danger',
  },
  {
    test: /^approve$/i,
    title: 'Confirm approval?',
    message: 'This approval changes the current verification or request status.',
    confirmText: 'Approve',
    variant: 'success',
  },
  {
    test: /^accept(?:\s+\(\+.*\))?$/i,
    title: 'Accept this request?',
    message: 'Accepting commits you to this request and changes its status immediately.',
    confirmText: 'Accept',
    variant: 'success',
  },
  {
    test: /^submit review$/i,
    title: 'Submit this review?',
    message: 'Your rating and review will be saved to this completed booking.',
    confirmText: 'Submit review',
    variant: 'warning',
  },
  {
    test: /^choose$/i,
    title: 'Choose this provider?',
    message: 'This will reassign the booking to the selected provider.',
    confirmText: 'Choose provider',
    variant: 'warning',
  },
  {
    test: /^dismiss$/i,
    title: 'Dismiss this cancellation review?',
    message: 'This resolves the cancellation review without applying a strike.',
    confirmText: 'Dismiss review',
    variant: 'warning',
  },
  {
    test: /^mark resolved$/i,
    title: 'Mark request resolved?',
    message: 'This clears the active additional-verification request for this provider.',
    confirmText: 'Mark resolved',
    variant: 'success',
  },
];

const normalise = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const findRule = (button) => {
  const explicit = normalise(button.dataset.confirmAction);
  const label = explicit || normalise(button.getAttribute('aria-label')) || normalise(button.title) || normalise(button.textContent);
  if (!label) return null;

  // These labels only close/reveal UI; they do not persist a state change.
  if (/^(cancel|close|go back|back|keep job|cancel this job|cancel this booking|refund|choose another gigger)$/i.test(label)) return null;

  return RULES.find((rule) => rule.test.test(label)) || null;
};

const ConfirmationGuard = ({ children }) => {
  const [pending, setPending] = useState(null);
  const replayingRef = useRef(false);

  const close = useCallback(() => setPending(null), []);

  const confirm = useCallback(() => {
    const next = pending;
    if (!next?.button) return;
    setPending(null);

    replayingRef.current = true;
    next.button.click();
    queueMicrotask(() => {
      replayingRef.current = false;
    });
  }, [pending]);

  useEffect(() => {
    const onClick = (event) => {
      if (replayingRef.current || event.defaultPrevented) return;
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-confirmation-modal="true"]')) return;

      const button = event.target.closest('button');
      if (!button || button.disabled || button.dataset.skipConfirmation === 'true') return;

      const rule = findRule(button);
      if (!rule) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();

      const title = button.dataset.confirmTitle || rule.title;
      const message = button.dataset.confirmMessage || rule.message;
      const confirmText = button.dataset.confirmText || rule.confirmText;
      const variant = button.dataset.confirmVariant || rule.variant;

      setPending({ button, title, message, confirmText, variant });
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  return (
    <>
      {children}
      <ConfirmationModal
        open={Boolean(pending)}
        title={pending?.title}
        message={pending?.message}
        confirmText={pending?.confirmText}
        variant={pending?.variant}
        onConfirm={confirm}
        onCancel={close}
      />
    </>
  );
};

export default ConfirmationGuard;
